import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const ToastContext = createContext(null);

export const useAppToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useAppToast must be used inside AppToastProvider");
  }
  return context;
};

const DEFAULT_DURATION = 4200;

export const AppToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    ({ title, message, variant = "error", duration = DEFAULT_DURATION }) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, title, message, variant }]);

      window.setTimeout(() => {
        removeToast(id);
      }, duration);
    },
    [removeToast]
  );

  const api = useMemo(
    () => ({
      showError: (message, title = "Request Failed") => showToast({ title, message, variant: "error" }),
      showSuccess: (message, title = "Success") => showToast({ title, message, variant: "success" }),
      showInfo: (message, title = "Info") => showToast({ title, message, variant: "info" })
    }),
    [showToast]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="vb-toast-stack" aria-live="assertive" aria-atomic="true">
        {toasts.map((toast) => (
          <div key={toast.id} className={`vb-toast vb-toast-${toast.variant}`}>
            <button
              className="vb-toast-close"
              type="button"
              aria-label="Close toast"
              onClick={() => removeToast(toast.id)}
            >
              x
            </button>
            <h4>{toast.title}</h4>
            <p>{toast.message}</p>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
